/**
 * @fileoverview Profile — the signed-in user's own account, at `/profile`.
 *
 * NOT AN APP. It is reached from the user menu, the way Foundry reaches its
 * own: the Applications launcher lists PRODUCTS, and "your own account" is not
 * one. Before this existed, clicking your own name in the user menu navigated
 * to `/settings` — the ORGANIZATION's settings — so asking to see yourself
 * landed on roles and permissions for everybody.
 *
 * WHAT IT SHOWS AND WHY THAT IS ALL. Every field here comes from the session
 * (`hex/ports/auth.ts`), which carries the claims the app actually consumes:
 * name, email, agency, role. Nothing is invented — there is no profile-update
 * endpoint behind this yet, so the page states what it knows and says plainly
 * where a value is changed instead of offering a control that would not work.
 * A disabled "Edit" button that has never been wired is worse than no button:
 * it promises a capability and spends the user's attention proving it absent.
 *
 * The Control Panel is linked rather than duplicated: identity here, the
 * organization there.
 */

import { Button, Icon, NonIdealState, Tag } from "@blueprintjs/core";
import { useNavigate } from "react-router";

import { Module, ModuleHeader } from "../components/module";
import { ContentCard, EntityRow } from "../components/control-panel";
import { useAuth } from "../contexts/auth-context";
import { CONTROL_PANEL_ROOT } from "../lib/control-panel-routes";

/** "Jonathan Agustin" -> "JA"; falls back to the email's first letter. */
function initialsOf(name: string | null, email: string): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return email.slice(0, 1).toUpperCase();
}

export function Profile() {
  const navigate = useNavigate();
  const { session } = useAuth();

  if (!session) {
    return (
      <Module>
        <ModuleHeader title="Profile" />
        <NonIdealState
          icon="user"
          title="Not signed in"
          description="Sign in to see your account."
        />
      </Module>
    );
  }

  const displayName = session.name ?? session.email;
  /*
    DON'T ECHO. Not every identity carries a `name` claim — the e2e user does
    not — and when it is absent the display name IS the email, so printing the
    email underneath rendered the same string twice, once large and once muted.
    A subtitle that repeats its heading is noise that looks like information.
  */
  const secondary = session.name ? session.email : null;

  return (
    <Module>
      <ModuleHeader title="Profile" />
      <div className="bp-control-panel bp-profile">
        <div className="bp-profile-identity">
          <span className="bp-profile-avatar" aria-hidden>
            {initialsOf(session.name, session.email)}
          </span>
          <div className="bp-profile-identity-text">
            <h2 className="bp6-heading bp-profile-name">{displayName}</h2>
            {secondary && <div className="bp6-text-muted">{secondary}</div>}
          </div>
        </div>

        <ContentCard title="Account">
          <EntityRow
            icon={<Icon icon="user" size={20} />}
            title={displayName}
            subtitleLines={[
              session.nickname ? `Known as ${session.nickname}` : null,
              secondary,
            ].filter(Boolean)}
          />
          <EntityRow
            icon={<Icon icon="shield" size={20} />}
            title={session.role ?? "No role assigned"}
            subtitleLines={[
              session.agencyId
                ? `Agency ${session.agencyId}`
                : "Not attached to an agency",
              /*
                Said in words rather than shown as a disabled control. Role and
                agency are granted by an administrator through the Control Panel;
                a user cannot change their own, and a greyed-out button here
                would imply they might.
              */
              "Changed by an administrator in the Control Panel",
            ]}
            actions={
              <Button
                minimal
                small
                icon="cog"
                text="Control Panel"
                onClick={() => navigate(CONTROL_PANEL_ROOT)}
              />
            }
          />
        </ContentCard>

        <ContentCard
          title="Session"
          titleCount={<Tag minimal>Active</Tag>}
        >
          <EntityRow
            icon={<Icon icon="key" size={20} />}
            title="Signed in"
            subtitleLines={[
              /*
                The subject claim, not the token. A profile page is a reasonable
                place to show WHICH identity is signed in and an unreasonable
                place to print a bearer token — it would put a live credential in
                screenshots, screen shares and support tickets.
              */
              `Subject ${session.sub}`,
            ]}
          />
        </ContentCard>
      </div>
    </Module>
  );
}
